const PENDING_DEEP_LINK_KEY = 'projects-pending-deep-link';
const PENDING_DEEP_LINK_COMPANY_KEY = 'projects-pending-deep-link-company';
const CONSUMED_DL_KEY = 'projects-consumed-deep-link';
const STALE_REDIRECT_KEY = 'projects-stale-deep-link-redirect';
const SESSION_MARKER_KEY = 'projects-deep-link-session';

let _deepLinkCompanySwitchActive = false;

/**
 * Returns true when a deep-link-triggered company switch is in progress.
 * Used by the company-change handler in AppContent to avoid redirecting
 * away from the deep-link target route.
 */
export function isDeepLinkCompanySwitch(): boolean {
  return _deepLinkCompanySwitchActive;
}

export function setDeepLinkCompanySwitch(active: boolean): void {
  _deepLinkCompanySwitchActive = active;
}

/**
 * Reads `page` and `entities` query params set by the Workspace Feed
 * deep-link ("View in Access Projects") and maps them to internal routes.
 *
 * Accepts either a raw search string (`?page=Projects&entities=5651`)
 * or a full URL / `widget://` URL from which the search portion is
 * extracted.
 *
 * Also handles the workspace `?location=` wrapper: when `page` is not
 * found at the top level, the function checks whether a `location`
 * param contains a `widget://` URL and extracts `page`/`entities`
 * from within it.
 *
 * Returns `null` when no recognised `page` param is present.
 */
export function getWidgetDeepLinkTarget(
  searchOrUrl: string = window.location.search,
): string | null {
  const search = extractSearchFromUrl(searchOrUrl);
  const params = new URLSearchParams(search);
  let page = params.get('page');
  let entityId = params.get('entities');

  // When the workspace wraps the widget URL inside a `?location=` param
  // (e.g. `?location=widget://access.erpprojects?page=Projects&entities=123`),
  // extract the inner params.
  if (!page) {
    const locationParam = params.get('location');
    if (locationParam) {
      const innerSearch = extractSearchFromUrl(locationParam);
      const innerParams = new URLSearchParams(innerSearch);
      page = innerParams.get('page');
      entityId = innerParams.get('entities');
    }
  }

  if (!page) return null;

  const pageMap: Record<string, string> = {
    Projects: entityId ? `/projects/${entityId}` : '/projects',
  };

  return pageMap[page] ?? null;
}

/**
 * Persists the resolved deep-link target to sessionStorage so it
 * survives URL changes (e.g. OIDC auth redirect stripping params).
 *
 * Call this as early as possible during app startup — before the
 * OIDC flow can strip query parameters from the URL.
 */
export function persistWidgetDeepLink(): void {
  const target = getWidgetDeepLinkTarget();
  if (!target) return;

  // If this exact target was already consumed in a previous session
  // and there is no session marker (meaning the browser was closed and
  // reopened), the workspace is restoring a stale deep-link URL.
  // Skip persisting and flag it so AppContent redirects to the landing
  // page instead.
  try {
    const consumed = localStorage.getItem(CONSUMED_DL_KEY);
    const marker = sessionStorage.getItem(SESSION_MARKER_KEY);
    if (consumed === target && !marker) {
      sessionStorage.setItem(STALE_REDIRECT_KEY, 'true');
      cleanWidgetParamsFromUrl();
      return;
    }
  } catch {
    // storage unavailable — fall through to normal persist
  }

  try {
    sessionStorage.setItem(PENDING_DEEP_LINK_KEY, target);
  } catch {
    // sessionStorage may be unavailable
  }

  // Persist companyId alongside the deep-link target so it survives OIDC redirects
  const companyId = getDeepLinkCompanyId();
  if (companyId) {
    persistDeepLinkCompanyId(companyId);
  }

  // Strip widget params from the URL so the workspace does not
  // persist them in its stored location state.  The target is already
  // saved to sessionStorage and will be consumed by DeepLinkHandler.
  cleanWidgetParamsFromUrl();
}

/**
 * Removes widget deep-link query parameters (`page`, `entities`) from
 * the current URL without triggering a navigation.  This prevents the
 * workspace from persisting stale deep-link params in its stored
 * iframe location, which would otherwise cause the app to re-open at
 * the deep-linked project on every subsequent visit.
 */
export function cleanWidgetParamsFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('page') && !url.searchParams.has('entities')) {
      return;
    }
    url.searchParams.delete('page');
    url.searchParams.delete('entities');
    window.history.replaceState({}, '', url.toString());
  } catch {
    // URL parsing or history API unavailable — ignore
  }
}

/**
 * Returns `true` when a pending widget deep-link exists in
 * sessionStorage.  Unlike `consumePendingWidgetDeepLink` this does
 * NOT remove the value — use it for guard checks that need to know
 * whether a deep-link navigation is pending without consuming it.
 */
export function hasPendingWidgetDeepLink(): boolean {
  try {
    return sessionStorage.getItem(PENDING_DEEP_LINK_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Reads and removes the pending widget deep-link from sessionStorage.
 * Used as a fallback when the URL params have been stripped by the
 * OIDC callback.
 */
export function consumePendingWidgetDeepLink(): string | null {
  try {
    const route = sessionStorage.getItem(PENDING_DEEP_LINK_KEY);
    if (route) {
      sessionStorage.removeItem(PENDING_DEEP_LINK_KEY);
      return route;
    }
  } catch {
    // sessionStorage may be unavailable
  }
  return null;
}

/**
 * Registers a one-time `message` event listener at module scope that
 * captures `NavigationRequest` postMessages from the parent workspace
 * and persists the resolved route to sessionStorage.
 *
 * The workspace sends this message when the user clicks a deep link
 * (e.g. "View in Access Projects" in the Feed).  The React-level
 * `WorkspaceNavigationListener` only mounts after `BootstrapGate`
 * finishes loading employee data, so messages that arrive during
 * bootstrap are lost.  This listener bridges that gap by capturing
 * the target before React renders.
 *
 * The listener removes itself after the first successful capture or
 * after a timeout to avoid leaking.
 */
export function listenForEarlyNavigationRequest(): void {
  const TIMEOUT_MS = 30_000;

  function handleMessage(event: MessageEvent): void {
    // If a stale restore was already detected, ignore further messages.
    try {
      if (sessionStorage.getItem(STALE_REDIRECT_KEY)) return;
    } catch { /* ignore */ }

    const data = parsePostMessageData(event.data);
    if (!data) return;

    if (
      typeof data.type === 'string' &&
      data.type.toLowerCase() === 'navigationrequest' &&
      typeof data.url === 'string'
    ) {
      const target = getWidgetDeepLinkTarget(data.url);
      if (target) {
        // Check if this is a stale restore (same target consumed in a
        // previous session with no session marker).
        try {
          const consumed = localStorage.getItem(CONSUMED_DL_KEY);
          const marker = sessionStorage.getItem(SESSION_MARKER_KEY);
          if (consumed === target && !marker) {
            sessionStorage.setItem(STALE_REDIRECT_KEY, 'true');
            window.removeEventListener('message', handleMessage);
            return;
          }
        } catch { /* ignore */ }

        try {
          sessionStorage.setItem(PENDING_DEEP_LINK_KEY, target);
        } catch {
          // sessionStorage may be unavailable
        }

        // Persist companyId from the navigation URL
        const companyId = getDeepLinkCompanyId(data.url);
        if (companyId) {
          persistDeepLinkCompanyId(companyId);
        }

        window.removeEventListener('message', handleMessage);
      }
    }
  }

  window.addEventListener('message', handleMessage);

  setTimeout(() => {
    window.removeEventListener('message', handleMessage);
  }, TIMEOUT_MS);
}

function parsePostMessageData(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === 'object' && raw !== null) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // Not JSON — ignore
    }
  }
  return null;
}

/**
 * Records that a deep-link target was consumed so that future app
 * loads can detect a stale workspace-restored URL.
 */
export function markDeepLinkConsumed(target: string): void {
  try {
    localStorage.setItem(CONSUMED_DL_KEY, target);
  } catch {
    // localStorage may be unavailable
  }
  try {
    sessionStorage.setItem(SESSION_MARKER_KEY, 'true');
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * Returns `true` when a session marker exists in sessionStorage,
 * indicating the app has already initialized in this browser session.
 * sessionStorage is cleared on browser close, so a missing marker
 * means this is a new session (e.g. the workspace restored a stale
 * iframe URL after a browser restart).
 */
export function hasSessionMarker(): boolean {
  try {
    return sessionStorage.getItem(SESSION_MARKER_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Sets the session marker in sessionStorage.  Call this during app
 * initialization so that page refreshes within the same session are
 * not mistaken for stale workspace restores.
 */
export function setSessionMarker(): void {
  try {
    sessionStorage.setItem(SESSION_MARKER_KEY, 'true');
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * If `persistWidgetDeepLink` or `listenForEarlyNavigationRequest`
 * flagged a stale workspace restore, this
 * consumes the flag and returns `true` so that the App can redirect
 * to the configured landing page.
 */
export function consumeStaleRedirectFlag(): boolean {
  try {
    if (sessionStorage.getItem(STALE_REDIRECT_KEY)) {
      sessionStorage.removeItem(STALE_REDIRECT_KEY);
      return true;
    }
  } catch {
    // sessionStorage unavailable
  }
  return false;
}

/**
 * Extracts the `companyId` query parameter from a deep-link URL or search string.
 * Checks both top-level params and the inner `?location=` wrapper.
 */
export function getDeepLinkCompanyId(
  searchOrUrl: string = window.location.search,
): string | null {
  const search = extractSearchFromUrl(searchOrUrl);
  const params = new URLSearchParams(search);
  let companyId = params.get('companyId');

  if (!companyId) {
    const locationParam = params.get('location');
    if (locationParam) {
      const innerSearch = extractSearchFromUrl(locationParam);
      const innerParams = new URLSearchParams(innerSearch);
      companyId = innerParams.get('companyId');
    }
  }

  return companyId;
}

/**
 * Persists a deep-link companyId to sessionStorage so it survives
 * OIDC redirects that strip query parameters.
 */
export function persistDeepLinkCompanyId(companyId: string): void {
  try {
    sessionStorage.setItem(PENDING_DEEP_LINK_COMPANY_KEY, companyId);
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * Reads and removes the pending deep-link companyId from sessionStorage.
 * Used by CompanyContext during initial load to select the correct company.
 */
export function consumeDeepLinkCompanyId(): string | null {
  try {
    const companyId = sessionStorage.getItem(PENDING_DEEP_LINK_COMPANY_KEY);
    if (companyId) {
      sessionStorage.removeItem(PENDING_DEEP_LINK_COMPANY_KEY);
      return companyId;
    }
  } catch {
    // sessionStorage may be unavailable
  }
  return null;
}

function extractSearchFromUrl(value: string): string {
  if (value.startsWith('?') || value === '') return value;

  if (value.startsWith('widget://')) {
    const qIndex = value.indexOf('?');
    return qIndex >= 0 ? value.slice(qIndex) : '';
  }

  try {
    return new URL(value).search;
  } catch {
    return value;
  }
}
